/**
 * Ürün kartındaki alternatif birimler.
 *
 * Stok DAİMA ürün kartının ANA biriminde tutulur. Talep/sipariş ise koli,
 * paket, çuval gibi alınış biriminde girilebilir; çevrimi sunucu yapar
 * (bkz. backend/routes/purchasing.py `_birim_carpani`). Buradaki hesap
 * yalnızca kullanıcıya seçenek sunmak ve karşılığını göstermek içindir —
 * kaydedilen çarpan her zaman sunucunun çözdüğüdür.
 *
 * `unit_conversions` kaydının okunuşu: 1 <from_unit> = factor <to_unit>.
 */

/**
 * Bir ürün için seçilebilir birimler.
 * @returns [{ deger, etiket, carpan }] — carpan: 1 <deger> kaç ANA birimdir.
 */
export function birimSecenekleri(product, units) {
  if (!product) return [];
  const ana = product.unit || 'adet';
  const kisaltma = (uid) => ((units || []).find(u => u.id === uid) || {}).abbreviation;
  // Ana birim, kısaltma ya da unit_id üzerinden tanınır (sunucu da böyle yapar)
  const anaMi = (uid) => uid === product.unit_id || kisaltma(uid) === ana;

  const liste = [{ deger: ana, etiket: ana, carpan: 1 }];
  (product.unit_conversions || []).forEach(c => {
    const kaynak = kisaltma(c.from_unit_id);
    const hedef = kisaltma(c.to_unit_id);
    const carpan = parseFloat(c.factor) || 0;
    if (!carpan) return;
    if (anaMi(c.to_unit_id) && kaynak && kaynak !== ana) {
      liste.push({ deger: kaynak, carpan, etiket: `${kaynak} (1 ${kaynak} = ${carpan} ${ana})` });
    } else if (anaMi(c.from_unit_id) && hedef && hedef !== ana) {
      const ters = +(1 / carpan).toFixed(6);
      liste.push({ deger: hedef, carpan: ters, etiket: `${hedef} (1 ${hedef} = ${ters} ${ana})` });
    }
  });
  return liste.filter((x, i, a) => a.findIndex(y => y.deger === x.deger) === i);
}

/**
 * Seçili birim ANA birimden farklıysa çevrim bilgisini döner, aynıysa null.
 * @returns { ana, carpan } | null
 */
export function anaBirimKarsiligi(product, units, secilenBirim) {
  if (!product) return null;
  const ana = product.unit || 'adet';
  if (!secilenBirim || secilenBirim === ana) return null;
  const secenek = birimSecenekleri(product, units).find(o => o.deger === secilenBirim);
  if (!secenek || !secenek.carpan) return null;
  return { ana, carpan: secenek.carpan };
}

/**
 * Sayım gibi ÖLÇÜLEN girişler için birim seçenekleri: kartın kendi
 * dönüşümleri (`birimSecenekleri`) + ANA BİRİMLE AYNI ÖLÇÜ AİLESİ (kg ürüne
 * "g", lt ürüne "ml"). Aile çevrimi sunucudaki `check_unit_compatibility`
 * kuralıyla aynı: yalnızca `quantity` dışındaki gruplarda (adet/koli gibi
 * birimlerin taban faktörü hepsi 1'dir, 1 koli 1 adet değildir).
 * Çarpan yalnızca ÖNİZLEME içindir; kaydedilen miktarı sunucu çevirir.
 * @returns [{ deger, etiket, carpan }] — carpan: 1 <deger> kaç ANA birim.
 */
export function olcuBirimleri(product, units) {
  const liste = birimSecenekleri(product, units);
  if (!product) return liste;
  const ana = (units || []).find(u => (u.abbreviation || '').toLowerCase() === (product.unit || '').toLowerCase());
  if (ana && ana.unit_group && ana.unit_group !== 'quantity') {
    const anaF = parseFloat(ana.to_base_factor) || 0;
    (units || []).forEach(u => {
      if (u.unit_group !== ana.unit_group || u.id === ana.id || !anaF) return;
      if (liste.some(o => (o.deger || '').toLowerCase() === (u.abbreviation || '').toLowerCase())) return;
      const carpan = (parseFloat(u.to_base_factor) || 0) / anaF;
      // Yalnızca bin kat yakınlıktaki birimler (kg ↔ g, lt ↔ ml/cl): mg ve ton
      // seçenek listesini kalabalıklaştırıyordu, sayımda kimse onlarla tartmaz.
      if (!(carpan >= 0.001 && carpan <= 1000) || (u.abbreviation || '').toLowerCase() === 'ton') return;
      liste.push({ deger: u.abbreviation, carpan, etiket: u.abbreviation });
    });
  }
  return liste;
}
